/**
 * EvaluatorBlock — renders the quality-check evaluator as a tool-call-style block.
 *
 * States:
 *   Running → spinner + purple border + live timer ("Finalizing shortlist...")
 *   Passed  → checkmark + green border ("Quality check passed")
 *   Failed  → warning + amber border + expandable issue list
 */

import { useState, useEffect } from "react";
import { useChatStore } from "@/stores/chatStore";
import { Spinner } from "@/components/common/Spinner";

export function EvaluatorBlock() {
  const evaluatorState = useChatStore((s) => s.evaluatorState);
  const [expanded, setExpanded] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!evaluatorState || evaluatorState.status !== "running") return;
    const interval = setInterval(() => {
      setElapsed(Date.now() - evaluatorState.startedAt);
    }, 100);
    return () => clearInterval(interval);
  }, [evaluatorState]);

  // Update elapsed on completion
  useEffect(() => {
    if (evaluatorState && evaluatorState.status !== "running") {
      setElapsed(Date.now() - evaluatorState.startedAt);
    }
  }, [evaluatorState?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!evaluatorState) return null;

  const { status, issues, rerunning } = evaluatorState;

  const borderClass = {
    running: "border-l-jove animate-[borderPulse_2s_ease-in-out_infinite]",
    passed: "border-l-emerald-500",
    failed: "border-l-amber-400",
  }[status];

  const bgClass = {
    running: "bg-space-20/40",
    passed: "bg-emerald-50/50",
    failed: "bg-amber-50/50",
  }[status];

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  };

  return (
    <div
      className={`border-l-[3px] ${borderClass} rounded-r-lg ${bgClass} overflow-hidden
        animate-[slideIn_200ms_ease-out] transition-colors duration-300`}
    >
      <button
        onClick={() => issues && issues.length > 0 && setExpanded(!expanded)}
        className={`w-full px-3 py-2 text-left transition-colors overflow-hidden
          ${issues && issues.length > 0 ? "hover:bg-space-20/80 cursor-pointer" : "cursor-default"}`}
      >
        <div className="flex items-center gap-2">
          {/* Status icon */}
          <div className="w-4 h-4 flex items-center justify-center">
            {status === "running" ? (
              <Spinner size="sm" className="text-jove" />
            ) : status === "passed" ? (
              <svg className="h-4 w-4 text-emerald-500 animate-[fadeInUp_150ms_ease-out]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="h-4 w-4 text-amber-500 animate-[fadeInUp_150ms_ease-out]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            )}
          </div>

          <span className="text-sm flex-1">
            <span className="font-medium text-space">
              {status === "running"
                ? "Finalizing shortlist..."
                : status === "passed"
                  ? "Quality check passed"
                  : `Found ${issues?.length ?? 0} issue${issues?.length !== 1 ? "s" : ""}`}
            </span>
            {rerunning && (
              <span className="text-space-40 font-normal"> — re-running with fixes</span>
            )}
          </span>

          {elapsed > 0 && (
            <span className="text-xs text-space-40 tabular-nums font-mono">
              {formatTime(elapsed)}
            </span>
          )}

          {issues && issues.length > 0 && (
            <svg
              className={`h-3.5 w-3.5 text-space-40 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </button>

      {/* Expanded issues list */}
      {expanded && issues && issues.length > 0 && (
        <div className="px-3 pb-3 animate-[slideDown_250ms_ease-out]">
          <div className="space-y-1.5 pl-6">
            {issues.map((issue, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className={`shrink-0 px-1.5 py-0.5 rounded font-semibold uppercase text-[9px] tracking-wide
                  ${issue.severity === "critical"
                    ? "bg-red-100 text-red-700"
                    : "bg-amber-100 text-amber-700"
                  }`}>
                  {issue.severity}
                </span>
                <span className="text-space-50">
                  <span className="font-medium text-space">{issue.program}:</span> {issue.issue}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
